;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import {
	LEASING_AGENT_ROLE_CATEGORY,
	type PropertyStaffRoleFilter,
} from "@/lib/services/property/property-staff-role";
import type { ChartOfAccountsEntry } from "@/lib/transformers/utils";
import type { TenantDirectoryEntry } from "@/lib/types/tenant";
import { normalizeVisibilityForUrl } from "@/lib/utils/property-visibility";
import type {
	Property as DbProperty,
	PropertyStaff as DbPropertyStaff,
} from "@/prisma/generated/client";
import { queryFetcher } from "@/queries/config/query-fetcher";
import { workspaceQueryKeys } from "@/queries/query-keys/workspace";
import type { ProviderType } from "@/types/enums";
import type { PropertyVisibilityFilter } from "@/types/property";
import type { WorkspaceMember } from "@/types/user";
import type { WorkspaceInvitation } from "@/types/workspace";

export interface Property extends DbProperty {
	id: string;
	name: string;
	propertyId: number;
	propertyGroups: PropertyGroup[];
}

export interface UnitDirectoryEntry {
	advertised_rent?: string | null;
	created_on?: string | null;
	deactivated_on?: string | null;
	is_active?: boolean;
	market_rent?: string | null;
	posted_to_internet?: string | null;
	posted_to_website?: string | null;
	property?: string | null;
	property_id?: number | null;
	property_name?: string | null;
	rent_ready?: string | null;
	rentable?: string | null;
	sqft?: number | null;
	unit_id: number;
	unit_tags?: string | null;
	unit_type?: string | null;
}

export interface ChartOfAccountsAvailability {
	hasAppFolioCredentials: boolean;
	hasSyncedAppFolioChartOfAccounts: boolean;
	isReady: boolean;
	lastSyncedAt: string | null;
}

export interface ReputationProperty {
	name: string;
	locationId: string;
}

export interface PropertyGroup {
	id: string;
	groupId: number;
	groupName: string;
	portfolio: string;
	organizationId: string;
	properties: Omit<Property, "propertyGroups">[];
}

interface PropertyStaff extends DbPropertyStaff {
	properties: DbProperty[];
}

/**
 * Fetches the current user's workspace
 * @returns The workspace data
 */
export async function getWorkspaces() {
	return queryFetcher("/api/workspaces", {
		method: "GET",
	});
}

/**
 * Fetches the current workspace users
 */
export async function getWorkspaceUsers(workspaceId: string): Promise<{
	users: WorkspaceMember[];
}> {
	return queryFetcher(`/api/workspaces/users?workspaceId=${workspaceId}`, {
		method: "GET",
	});
}

export async function getWorkspaceUserCount(workspaceId: string): Promise<{
	count: number;
}> {
	return queryFetcher(
		`/api/workspaces/users?workspaceId=${workspaceId}&countOnly=1`,
		{
			method: "GET",
		},
	);
}

/**
 * Fetches the user invitations for the workspace
 */
export async function getWorkspaceUserInvitations(
	workspaceId: string,
): Promise<{ data: WorkspaceInvitation[] }> {
	return queryFetcher(`/api/workspaces/${workspaceId}/invitations`, {
		method: "GET",
	});
}

// Fetches the properties for a workspace
export async function getWorkspaceProperties(
	workspaceId: string,
	shouldFilterByPropertyGroupIds = false,
	visibility?: PropertyVisibilityFilter,
	includeInactive = false,
): Promise<Property[]> {
	const params = new URLSearchParams();
	if (shouldFilterByPropertyGroupIds) {
		params.append("shouldFilterByPropertyGroupIds", "true");
	}
	// Only add visibility to URL when not "All" (undefined/All = show all)
	const normalizedVisibility = normalizeVisibilityForUrl(visibility);
	if (normalizedVisibility) {
		params.append("visibility", normalizedVisibility);
	}
	if (includeInactive) {
		params.append("includeInactive", "true");
	}
	const queryString = params.toString();
	const url = `/api/workspaces/${workspaceId}/properties${queryString ? `?${queryString}` : ""}`;

	return queryFetcher(url, {
		method: "GET",
	});
}

// Fetches the property groups for a workspace
export async function getWorkspacePropertyGroups(
	workspaceId: string,
	shouldFilterByPropertyGroupIds = false,
	visibility?: PropertyVisibilityFilter,
): Promise<PropertyGroup[]> {
	const params = new URLSearchParams();
	if (shouldFilterByPropertyGroupIds) {
		params.append("shouldFilterByPropertyGroupIds", "true");
	}
	// Only add visibility to URL when not "All" (undefined/All = show all)
	const normalizedVisibility = normalizeVisibilityForUrl(visibility);
	if (normalizedVisibility) {
		params.append("visibility", normalizedVisibility);
	}
	const queryString = params.toString();
	const url = `/api/workspaces/${workspaceId}/property-groups${queryString ? `?${queryString}` : ""}`;

	return queryFetcher(url, {
		method: "GET",
	});
}

// Fetches the reputation properties for a workspace
export async function getWorkspaceReputationProperties(
	workspaceId: string,
): Promise<ReputationProperty[]> {
	return queryFetcher(`/api/workspaces/${workspaceId}/reputation-properties`, {
		method: "GET",
	});
}

// Fetches the property staff for a workspace
export async function getWorkspacePropertyStaff({
	providerType,
	roleFilter,
	shouldFilterByPropertyGroupIds = false,
	workspaceId,
}: {
	providerType?: ProviderType;
	roleFilter?: PropertyStaffRoleFilter | PropertyStaffRoleFilter[];
	shouldFilterByPropertyGroupIds?: boolean;
	workspaceId: string;
}): Promise<PropertyStaff[]> {
	const params = new URLSearchParams();
	if (shouldFilterByPropertyGroupIds) {
		params.append("shouldFilterByPropertyGroupIds", "true");
	}
	if (roleFilter) {
		const fragments = Array.isArray(roleFilter) ? roleFilter : [roleFilter];
		for (const fragment of fragments) {
			if (typeof fragment === "string") {
				const cleanedRole = fragment.trim();
				if (cleanedRole.length > 0) {
					params.append("role", cleanedRole);
				}
				continue;
			}

			if (
				fragment.kind === LEASING_AGENT_ROLE_CATEGORY.kind &&
				fragment.category === LEASING_AGENT_ROLE_CATEGORY.category
			) {
				params.append("roleCategory", fragment.category);
			}
		}
	}
	if (providerType) {
		params.append("providerType", providerType);
	}
	const queryString = params.toString();
	const url = `/api/workspaces/${workspaceId}/property-staff${
		queryString ? `?${queryString}` : ""
	}`;

	return queryFetcher(url, {
		method: "GET",
	});
}

/** ---- Functions with query options ---- */
/**
 * Returns a queryOptions object for the getWorkspace function
 * @param workspaceId - The ID of the workspace to get
 * @returns A queryOptions object
 */
export const getWorkspacesQueryOptions = () => {
	return queryOptions({
		queryFn: getWorkspaces,
		queryKey: workspaceQueryKeys.lists(),
		throwOnError: false,
	});
};

export const getWorkspaceUserCountQueryOptions = (workspaceId: string) => {
	return queryOptions({
		enabled: !!workspaceId,
		queryFn: () => getWorkspaceUserCount(workspaceId),
		queryKey: workspaceQueryKeys.userCount(workspaceId),
		staleTime: 60 * 1000,
	});
};

export const getWorkspaceUsersQueryOptions = (workspaceId: string) => {
	return queryOptions({
		enabled: !!workspaceId,
		queryFn: () => getWorkspaceUsers(workspaceId),
		queryKey: workspaceQueryKeys.users(workspaceId),
		select: (response) => {
			const sortedUsers = [...(response.users ?? [])].sort(
				(a, b) =>
					new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
			);

			return sortedUsers;
		},
		throwOnError: false,
	});
};

export const getWorkspaceUserInvitationsQueryOptions = (
	workspaceId: string,
) => {
	return queryOptions({
		enabled: !!workspaceId,
		queryFn: () => getWorkspaceUserInvitations(workspaceId),
		queryKey: workspaceQueryKeys.invitations(workspaceId),
		throwOnError: false,
	});
};

export const getWorkspacePropertiesQueryOptions = (
	workspaceId: string,
	shouldFilterByPropertyGroupIds = false,
	visibility?: PropertyVisibilityFilter,
	includeInactive = false,
) => {
	// Normalize visibility for consistent cache keys (undefined and "All" are equivalent)
	const normalizedVisibility = normalizeVisibilityForUrl(visibility);
	return queryOptions({
		enabled: !!workspaceId,
		placeholderData: keepPreviousData,
		queryFn: () =>
			getWorkspaceProperties(
				workspaceId,
				shouldFilterByPropertyGroupIds,
				visibility,
				includeInactive,
			),
		queryKey: workspaceQueryKeys.properties({
			includeInactive,
			shouldFilterByPropertyGroupIds,
			visibility: normalizedVisibility,
			workspaceId,
		}),
	});
};

export const getWorkspacePropertyGroupsQueryOptions = (
	workspaceId: string,
	shouldFilterByPropertyGroupIds = false,
	visibility?: PropertyVisibilityFilter,
) => {
	// Normalize visibility for consistent cache keys (undefined and "All" are equivalent)
	const normalizedVisibility = normalizeVisibilityForUrl(visibility);
	return queryOptions({
		enabled: !!workspaceId,
		placeholderData: keepPreviousData,
		queryFn: () =>
			getWorkspacePropertyGroups(
				workspaceId,
				shouldFilterByPropertyGroupIds,
				visibility,
			),
		queryKey: workspaceQueryKeys.propertyGroups({
			shouldFilterByPropertyGroupIds,
			visibility: normalizedVisibility,
			workspaceId,
		}),
	});
};

export const getWorkspacePropertyStaffQueryOptions = ({
	enabled = true,
	providerType,
	roleFilter,
	shouldFilterByPropertyGroupIds = false,
	workspaceId,
}: {
	enabled?: boolean;
	providerType?: ProviderType;
	roleFilter?: PropertyStaffRoleFilter | PropertyStaffRoleFilter[];
	shouldFilterByPropertyGroupIds?: boolean;
	workspaceId: string;
}) => {
	return queryOptions({
		enabled: Boolean(workspaceId) && enabled,
		placeholderData: keepPreviousData,
		queryFn: () =>
			getWorkspacePropertyStaff({
				providerType,
				roleFilter,
				shouldFilterByPropertyGroupIds,
				workspaceId,
			}),
		queryKey: workspaceQueryKeys.propertyStaff({
			providerType,
			roleFilter,
			shouldFilterByPropertyGroupIds,
			workspaceId,
		}),
	});
};

export const getWorkspaceReputationPropertiesQueryOptions = (
	workspaceId: string,
) => {
	return queryOptions({
		enabled: !!workspaceId,
		placeholderData: keepPreviousData,
		queryFn: () => getWorkspaceReputationProperties(workspaceId),
		queryKey: workspaceQueryKeys.reputationProperties(workspaceId),
	});
};

// Fetches the chart of accounts for a workspace
export async function getWorkspaceChartOfAccounts(
	workspaceId: string,
): Promise<{
	chartOfAccounts: ChartOfAccountsEntry[];
	lastSyncedAt: string | null;
}> {
	return queryFetcher(`/api/workspaces/${workspaceId}/chart-of-accounts`, {
		method: "GET",
	});
}

export const getWorkspaceChartOfAccountsQueryOptions = (
	workspaceId: string,
) => {
	return queryOptions({
		enabled: !!workspaceId,
		placeholderData: keepPreviousData,
		queryFn: () => getWorkspaceChartOfAccounts(workspaceId),
		queryKey: workspaceQueryKeys.chartOfAccounts(workspaceId),
	});
};

export async function getWorkspaceChartOfAccountsAvailability(
	workspaceId: string,
): Promise<ChartOfAccountsAvailability> {
	return queryFetcher(
		`/api/workspaces/${workspaceId}/chart-of-accounts-availability`,
		{
			method: "GET",
		},
	);
}

export const getWorkspaceChartOfAccountsAvailabilityQueryOptions = (
	workspaceId: string,
) => {
	return queryOptions({
		enabled: !!workspaceId,
		placeholderData: keepPreviousData,
		queryFn: () => getWorkspaceChartOfAccountsAvailability(workspaceId),
		queryKey: workspaceQueryKeys.chartOfAccountsAvailability(workspaceId),
	});
};

export async function getWorkspaceUnits(workspaceId: string): Promise<{
	lastSyncedAt: string | null;
	units: UnitDirectoryEntry[];
}> {
	return queryFetcher(`/api/workspaces/${workspaceId}/units`, {
		method: "GET",
	});
}

export const getWorkspaceUnitsQueryOptions = (workspaceId: string) => {
	return queryOptions({
		enabled: !!workspaceId,
		placeholderData: keepPreviousData,
		queryFn: () => getWorkspaceUnits(workspaceId),
		queryKey: workspaceQueryKeys.units(workspaceId),
	});
};

export async function getWorkspaceTenants(
	workspaceId: string,
): Promise<TenantDirectoryEntry[]> {
	return queryFetcher(`/api/workspaces/${workspaceId}/tenants`, {
		method: "GET",
	});
}

export const getWorkspaceTenantsQueryOptions = (workspaceId: string) => {
	return queryOptions({
		enabled: !!workspaceId,
		placeholderData: keepPreviousData,
		queryFn: () => getWorkspaceTenants(workspaceId),
		queryKey: workspaceQueryKeys.tenants(workspaceId),
	});
};
